import {
  combineLatest,
  distinctUntilKeyChanged,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { subjectsComplete } from 'src/app/common/app.helpers';
import { FieldAttribute, FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { CommunityFocus } from 'src/app/swagger-api';
import { MaybeUndefined } from 'src/app/utils';
import { UserTypes } from '../profile/profile.enum';
import { CommunityFormData } from './create-community.interfaces';

export type FieldState<TValue> = {
  value: TValue;
  hidden: boolean;
  disabled: boolean;
};

type FieldVisibilityRule = {
  when: (userTypes: UserTypes, model: Partial<CommunityFormData>) => boolean;
};

type FieldAttributeUpdating = (
  model: Partial<CommunityFormData>,
  userType: UserTypes,
) => (fieldAttribute: FieldAttribute) => FieldAttribute;

const VISIBILITY_RULES = new Map<keyof CommunityFormData, FieldVisibilityRule>([
  ['focus', { when: (userType, _formModel) => userType === UserTypes.ORGANIZATION }],
  ['diseases', { when: (_userType, formModel) => formModel?.focus !== 'CHARITY' }],
  ['specialities', { when: (_userType, formModel) => formModel?.focus !== 'CHARITY' }],
  ['charity_focuses', { when: (_userType, formModel) => formModel?.focus === 'CHARITY' }],
]);

export const updateHiddenAtribute: FieldAttributeUpdating =
  (formModel: Partial<CommunityFormData>, userType: UserTypes) =>
  (fieldAttribute: FieldAttribute): FieldAttribute => {
    const rule: MaybeUndefined<FieldVisibilityRule> = VISIBILITY_RULES.get(
      fieldAttribute.backendAttribute.name as keyof CommunityFormData,
    );

    if (!rule) {
      return fieldAttribute;
    }

    const isVisible = rule.when(userType, formModel);

    return {
      ...fieldAttribute,
      hidden: !isVisible,
    };
  };

export class CommunityFormState {
  private _destroy$: Subject<void> = new Subject<void>();
  private _userType$: ReplaySubject<UserTypes> = new ReplaySubject(1);
  private _attributes$: ReplaySubject<FormConstructorContent> = new ReplaySubject(1);

  public get formContent$(): Observable<FormConstructorContent> {
    return combineLatest([this._userType$, this._attributes$]).pipe(
      map(([userType, attributes]: [UserTypes, FormConstructorContent]) => ({
        ...attributes,
        fieldAttributes: attributes.fieldAttributes.map(updateHiddenAtribute(attributes.data, userType)),
      })),
      shareReplay(1),
    );
  }

  public get focusState$(): Observable<FieldState<CommunityFocus>> {
    return combineLatest([
      this.formContent$.pipe(
        map(({ fieldAttributes }) => fieldAttributes.find(({ backendAttribute }) => backendAttribute.name === 'focus')),
      ),
      this.formValue$.pipe(
        distinctUntilKeyChanged('focus'),
        map(({ focus }) => focus),
      ),
    ]).pipe(
      map(([attribute, focus]) => ({ value: focus, hidden: attribute.hidden, disabled: attribute.disabled })),
      startWith({ value: 'HEALTH', hidden: true, disabled: true } satisfies FieldState<CommunityFocus>),
    );
  }

  public constructor(
    private formValue$: Observable<CommunityFormData>,
    private attributes$: Observable<FormConstructorContent>,
    private userType$: Observable<UserTypes>,
  ) {
    this.userType$.pipe(takeUntil(this._destroy$)).subscribe((userType: UserTypes) => {
      this._userType$.next(userType);
    });

    this.attributes$.pipe(takeUntil(this._destroy$)).subscribe((attributes: FormConstructorContent) => {
      this._attributes$.next(attributes);
    });

    this.formValue$
      .pipe(distinctUntilKeyChanged('focus'))
      .pipe(withLatestFrom(this._attributes$.asObservable()))
      .pipe(takeUntil(this._destroy$))
      .subscribe(([formModel, attributes]: [CommunityFormData, FormConstructorContent]) => {
        this._attributes$.next({ ...attributes, data: formModel });
      });
  }

  public destroy(): void {
    subjectsComplete([this._destroy$, this._userType$, this._attributes$]);
  }
}
